import { useEffect, useMemo, useState } from 'react';

import { useDispatch } from 'react-redux';

import fakeFileImgPath from 'assets/fileTypes/fake-file.svg';
import classNames from 'classnames';
import { getJsonFromStorage } from 'helpers/localStorageHelper';

import { sendAnalyticEvent } from 'data/actions/analytics';

import { generatePDFCover } from 'utils/generatePdfCover';

import { LOCAL_STORAGE_KEYS } from 'ts/constants/local-storage-keys';
import type { ApiFile } from 'ts/types/file';
import { InternalFileType } from 'ts/types/file';

export const FilePreview = ({
  src,
  width,
  className
}: {
  src: string | Blob | null;
  width?: number;
  className?: string;
}) => {
  const [error, setError] = useState(false);

  const imgSrc = useMemo(() => {
    if (error || !src) return fakeFileImgPath;
    return typeof src === 'string' ? src : URL.createObjectURL(src);
  }, [src, error]);

  return (
    <img
      className={classNames('object-contain max-w-full max-h-full', className)}
      width={width}
      src={imgSrc}
      alt='file preview'
      onError={() => setError(true)}
    />
  );
};

interface UseDocumentPreviewProps {
  file?: ApiFile;
  fileUrl: string;
  formatTo: InternalFileType;
  formatFrom: InternalFileType;
  width?: number;
  isDownloadFile?: boolean;
}

const imagesFormat = new Set([
  InternalFileType.HEIC,
  InternalFileType.SVG,
  InternalFileType.PNG,
  InternalFileType.BMP,
  InternalFileType.EPS,
  InternalFileType.GIF,
  InternalFileType.TIFF,
  InternalFileType.WEBP,
  InternalFileType.JPG,
  InternalFileType.JPEG
]);

export const useDocumentPreview = ({
  file,
  fileUrl,
  formatTo,
  formatFrom = InternalFileType.PDF,
  width = 640,
  isDownloadFile = false
}: UseDocumentPreviewProps) => {
  const [image, setImage] = useState<string | Blob | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const convertDocumentData = getJsonFromStorage(LOCAL_STORAGE_KEYS.DATA_TO_CONVERT);
  const editDocumentData = getJsonFromStorage(LOCAL_STORAGE_KEYS.DATA_TO_EDIT);

  const fileContent = useMemo(() => {
    if (isDownloadFile) {
      return fileUrl;
    }

    if (editDocumentData?.fileBase64) {
      return editDocumentData.fileBase64;
    }

    if (convertDocumentData?.fileBase64) {
      return convertDocumentData.fileBase64;
    }

    return fileUrl;
  }, [editDocumentData?.fileBase64, convertDocumentData?.fileBase64, fileUrl, isDownloadFile]);

  const dispatch = useDispatch();

  useEffect(() => {
    const showPlaceholder =
      ![InternalFileType.PDF, InternalFileType.JPEG, InternalFileType.JPG, InternalFileType.PNG].includes(formatFrom) ||
      formatTo === InternalFileType.TXT;

    const loadPreview = async () => {
      setLoading(true);
      setError(false);

      const formatToDisplay = !showPlaceholder ? formatFrom : formatTo;

      try {
        if (!showPlaceholder && (formatToDisplay || file?.internal_type) === InternalFileType.PDF) {
          const pdfCover = await generatePDFCover({
            pdfFileUrl: fileContent,
            width,
            throwError: true
          });
          setImage(pdfCover);
          dispatch(
            sendAnalyticEvent({
              event: 'preview_status_view',
              data: { status: 'success' }
            })
          );
        } else if (!showPlaceholder && imagesFormat.has(formatToDisplay) && fileUrl) {
          dispatch(
            sendAnalyticEvent({
              event: 'preview_status_view',
              data: { status: 'success' }
            })
          );
          setImage(fileUrl);
        } else {
          dispatch(
            sendAnalyticEvent({
              event: 'preview_status_view',
              data: { status: 'impossible' }
            })
          );
          setImage(fakeFileImgPath);
        }
      } catch {
        setError(true);
        setImage(fakeFileImgPath);
        dispatch(
          sendAnalyticEvent({
            event: 'preview_status_view',
            data: { status: 'error' }
          })
        );
      } finally {
        setLoading(false);
      }
    };

    if ((isDownloadFile && !file) || (image && image !== fakeFileImgPath)) {
      return;
    }

    loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, fileContent]);

  return { image, loading, error };
};
